<template>
  <el-container class="address-book-page">
    <!-- 左侧树 -->
    <resizable-aside class="aside" :default-width="300" :min-width="200" :max-width="500" storage-key="address-book-aside-width">
      <div class="header">
        <el-input
            v-model="keyword"
            size="small"
            placeholder="搜索姓名/部门"
            prefix-icon="el-icon-search"
            clearable
        />
      </div>

      <div class="tree-wrap">
        <virtual-scroller
            :key="virtualScrollerKey"
            ref="virtualScroller"
            :items="flattenedTreeData"
            :size="40"
            class="virtual-scroller-container"
        >
          <template v-slot="{ item }">
            <div
                class="tree-node-wrapper"
                :style="{ paddingLeft: item.level * 12 + 'px' }"
                :class="{ active: activeNodeId === item.id }"
                @click="onNodeClick(item)"
                @contextmenu.prevent="item.type === 'USER' && showRightMenu($event, item)"
            >
              <!-- 部门 -->
              <div v-if="item.type === 'DEPT'" class="tree-node dept-node">
                <i
                    class="iconfont"
                    :class="expandedKeys.includes(item.id) ? 'icon-icon_folder_open1' : 'icon-icon_folder1'"
                    @click.stop="toggleExpand(item)"
                />
                <span class="dept-name">{{ item.name }}</span>
                <span class="dept-count">({{ item.userCount }})</span>
              </div>

              <!-- 人员 -->
              <div v-else class="tree-node user-node">
                <head-image :name="item.nickname" :size="24" :url="item.avatar" />
                <span class="nickname">{{ item.nickname }}</span>
              </div>
            </div>
          </template>
        </virtual-scroller>
      </div>
    </resizable-aside>

    <!-- 右侧详情 -->
    <el-main class="main">
      <div v-if="!userInfo.id" class="empty-tip">
        <no-data-tip text="请选择联系人" />
      </div>

      <div v-else class="detail">
        <div class="header">{{ userInfo.nickName }}</div>
        <div class="info">
          <head-image
              :size="160"
              :name="userInfo.nickName"
              :url="userInfo.headImageThumb"
              :radius="'10%'"
              @click.native="showFullImage"
          />
          <el-descriptions :column="1" border class="user-descriptions">
            <el-descriptions-item label="部门">{{ userInfo.deptName }}</el-descriptions-item>
            <el-descriptions-item label="昵称">{{ userInfo.nickName }}</el-descriptions-item>
            <el-descriptions-item label="性别">{{ userInfo.sex == 1 ? '男' : '女' }}</el-descriptions-item>
            <el-descriptions-item label="签名">{{ userInfo.signature }}</el-descriptions-item>
          </el-descriptions>
        </div>

        <div class="btn-group">
          <el-button type="primary" icon="el-icon-chat-line-round" @click="onSendMessage(userInfo)">
            发消息
          </el-button>
        </div>
      </div>
    </el-main>

    <right-menu ref="rightMenu" @select="onSelectMenu" />
    <chat-selector ref="chatSel" title="分享名片" />
    <friend-apply ref="applyRef" />
  </el-container>
</template>

<script>
import ResizableAside from '../components/common/ResizableAside.vue';

export default {
  name: 'AddressBook',
  components: {
    HeadImage: () => import('@/components/common/HeadImage.vue'),
    NoDataTip: () => import('@/components/common/NoDataTip.vue'),
    RightMenu: () => import('@/components/common/RightMenu.vue'),
    ChatSelector: () => import('@/components/chat/ChatSelector.vue'),
    FriendApply: () => import('@/components/friend/FriendApply.vue'),
    VirtualScroller: () => import('@/components/common/VirtualScroller.vue'),
    ResizableAside
  },
  data() {
    return {
      keyword: '',
      virtualScrollerKey: 0,
      allDepts: [],           // 冻结后的原始数据
      treeData: [],           // 当前渲染的树（冻结）
      expandedKeys: [],       // 展开节点ID数组（模板绑定）
      activeNodeId: null,
      flattenedTreeData: [],  // 虚拟滚动数据（冻结）
      userInfo: {},
      menuItems: [
        { key: 'CHAT', name: '发送消息' },
        { key: 'CARD', name: '分享名片' }
      ],

      // 优化新增
      flatUsers: [],          // 扁平用户列表（冻结）
      userMap: new Map(),     // 用户ID快速查找Map
      searchTimer: null       // 防抖计时器
    };
  },

  mounted() {
    this.loadAll();
  },

  methods: {
    /* ----------------- 数据获取 ----------------- */
    loadAll() {
      this.$http.get('/addressBook/user').then(res => {
        // 核心优化：冻结原始数据，切断Vue响应式劫持
        this.allDepts = Object.freeze(res || []);

        // 构建用户索引，用于极速搜索
        this.buildUserIndex(this.allDepts);

        // 默认展开第一个根节点（如果有）
        if (this.allDepts.length > 0) {
          this.expandedKeys.push(this.allDepts[0].id);
        }

        this.refreshTree();
      });
    },

    // 预构建用户索引：提取所有用户到扁平数组，建立ID映射
    buildUserIndex(depts) {
      const users = [];
      const map = new Map();

      const traverse = (list) => {
        list.forEach(d => {
          if (d.users) {
            d.users.forEach(u => {
              users.push(u);
              map.set(u.id, u);
            });
          }
          if (d.children) traverse(d.children);
        });
      };

      traverse(depts);
      this.flatUsers = Object.freeze(users);
      this.userMap = map;
    },

    /* ----------------- 树构造（核心优化） ----------------- */
    buildTree(depts) {
      const kw = (this.keyword || '').trim();

      // 搜索模式：直接返回虚拟搜索结果节点，不递归原树
      if (kw) {
        return this.buildSearchResult(kw);
      }

      // 正常模式：构建部门树
      return this.buildNormalTree(depts);
    },

    buildNormalTree(depts) {
      const build = (list) => {
        return list.map(d => {
          const children = d.children ? build(d.children) : [];
          const users = (d.users || []).map(u => ({
            id: u.id,
            nickname: u.nickname,
            avatar: u.headImageThumb,
            deptName: d.name,
            type: 'USER'
          }));

          return Object.freeze({
            ...d,
            userCount: users.length,
            children: Object.freeze([...children, ...users])
          });
        });
      };

      return Object.freeze(build(depts));
    },

    // 搜索优化：基于 flatUsers 过滤，O(N) 复杂度，N=4000
    buildSearchResult(kw) {
      const key = kw.toLowerCase();

      // 直接从 flatUsers 过滤，避免递归整棵树
      const matched = this.flatUsers.filter(u =>
          (u.nickname || '').toLowerCase().includes(key) ||
          (u.userName || '').toLowerCase().includes(key)
      );

      // 构造虚拟部门节点容纳结果
      return Object.freeze([{
        id: 'SEARCH_RESULT',
        name: `搜索结果 (${matched.length})`,
        type: 'DEPT',
        userCount: matched.length,
        children: Object.freeze(matched.map(u => ({
          id: u.id,
          nickname: u.nickname,
          avatar: u.headImageThumb,
          deptName: u.deptName || '未知部门',
          type: 'USER'
        })))
      }]);
    },

    /* ----------------- 树扁平化（栈替代递归） ----------------- */
    flattenTree(treeData) {
      if (!treeData || !treeData.length) return [];

      const res = [];
      // 临时 Set 加速展开状态判断，避免大量 includes 操作
      const expandedSet = new Set(this.expandedKeys);
      const stack = [];

      // 倒序入栈保证正序输出
      for (let i = treeData.length - 1; i >= 0; i--) {
        stack.push({ node: treeData[i], level: 0 });
      }

      while (stack.length > 0) {
        const { node, level } = stack.pop();

        // 添加当前节点
        res.push({ ...node, level });

        // 如果是部门且已展开，将子节点倒序入栈
        if (node.type !== 'USER' && expandedSet.has(node.id) && node.children?.length) {
          const children = node.children;
          for (let i = children.length - 1; i >= 0; i--) {
            stack.push({ node: children[i], level: level + 1 });
          }
        }
      }

      // 冻结结果，避免Vue监听虚拟列表大数组
      return Object.freeze(res);
    },

    toggleExpand(node) {
      const idx = this.expandedKeys.indexOf(node.id);
      if (idx > -1) {
        this.expandedKeys.splice(idx, 1);
      } else {
        this.expandedKeys.push(node.id);
      }

      this.$nextTick(() => {
        requestAnimationFrame(() => {
          // 仅重新扁平化，不重建树结构
          this.flattenedTreeData = this.flattenTree(this.treeData);
          this.virtualScrollerKey += 1;
        });
      });
    },

    // 防抖刷新：用于关键词搜索
    refreshTree() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        requestAnimationFrame(() => {
          this.treeData = this.buildTree(this.allDepts);
          this.flattenedTreeData = this.flattenTree(this.treeData);
          this.virtualScrollerKey += 1;
        });
      }, 300);
    },

    /* ----------------- 事件处理 ----------------- */
    onNodeClick(data) {
      this.activeNodeId = data.id;
      if (data.type === 'USER') {
        // 优先从 Map 快速取数据，避免请求接口
        const cachedUser = this.userMap.get(data.id);
        if (cachedUser) {
          this.userInfo = { ...cachedUser, deptName: data.deptName };
        } else {
          // 兜底请求
          this.$http.get(`/user/find/${data.id}`).then(user => {
            this.userInfo = { ...user, deptName: data.deptName };
          });
        }
      } else {
        this.toggleExpand(data);
      }
    },

    onSendMessage(friend) {
      this.$http
          .post('/friend/add', null, { params: { friendId: friend.id } })
          .then(() => {
            const chat = {
              type: 'PRIVATE',
              targetId: friend.id,
              showName: friend.nickname,
              headImage: friend.headImage,
              isDnd: friend.isDnd,
              isTop: friend.isTop
            };
            this.chatStore.openChat(chat);
            this.chatStore.setActiveChat(chat);
            this.$router.push('/home/chat');
          });
    },

    onAddFriend() {
      this.$refs.applyRef?.open(this.userInfo);
    },

    showRightMenu(e, user) {
      this.userInfo = user;
      this.userInfo.nickName = user.nickname;
      this.$refs.rightMenu.open(e, this.menuItems);
    },

    onSelectMenu(item) {
      switch (item.key) {
        case 'CHAT':
          this.onSendMessage(this.userInfo);
          break;
        case 'CARD':
          this.onSendCard(this.userInfo);
          break;
        case 'ADD':
          this.onAddFriend();
          break;
      }
    },

    onSendCard(friend) {
      this.$refs.chatSel.open(chats => {
        let idx = 0;
        chats.forEach(chat => {
          const cardData = {
            userId: friend.id,
            nickName: friend.nickname,
            headImage: friend.avatar
          };
          const msgInfo = {
            type: this.$enums.MESSAGE_TYPE.USER_CARD,
            content: JSON.stringify(cardData),
            recvId: chat.type === 'PRIVATE' ? chat.targetId : undefined,
            groupId: chat.type === 'GROUP' ? chat.targetId : undefined
          };
          const action = `/message/${chat.type.toLowerCase()}/send`;
          this.$http({ url: action, method: 'post', data: msgInfo }).then(m => {
            m.selfSend = true;
            this.chatStore.openChat(chat);
            this.chatStore.insertMessage(m, chat);
            if (++idx === chats.length) this.$message.success('发送成功');
          });
        });
      });
    },

    showFullImage() {
      if (this.userInfo.headImage) {
        this.$eventBus.$emit('openFullImage', this.userInfo.headImage);
      }
    }
  },

  watch: {
    keyword() {
      // 使用防抖刷新，避免输入过程卡顿
      this.refreshTree();
    }
  }
};
</script>

<style scoped>
.address-book-page {
  height: 100vh;
  display: flex;
}

.address-book-page .header {
  padding: 10px;
  border-bottom: 1px solid #e8e8e8;
}

.aside {
  background: #fcfdff;
  border-right: 1px solid #e8e8e8;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.aside .tree-wrap {
  flex: 1;
  overflow: hidden;
}

.virtual-scroller-container {
  height: 100%;
  overflow-y: auto;
}

.main {
  padding: 0;
  height: 100%;
  overflow: hidden;
}

.main .empty-tip {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}

.main .header {
  padding: 12px;
  font-size: 18px;
  border-bottom: 1px solid #e8e8e8;
}

.main .info {
  display: flex;
  padding: 20px;
  justify-content: center;
}

.main .info .user-descriptions {
  margin-left: 20px;
  width: 300px;
}

.main .btn-group {
  padding: 0 20px 20px;
  display: flex;
  justify-content: center;
}

.tree-node-wrapper {
  height: 40px;
  line-height: 40px;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: all 0.2s ease;
  border-radius: 4px;
  margin: 2px 8px;
}

.tree-node-wrapper:hover {
  background: #e6f4ff;
  transform: translateX(2px);
}

.tree-node-wrapper.active {
  background: #d8edff;
  border-left: 3px solid #409eff;
}

.tree-node {
  display: flex;
  align-items: center;
  font-size: 14px;
  width: 100%;
  height: 100%;
}

.tree-node .iconfont {
  margin-right: 6px;
  color: #606266;
  cursor: pointer;
  flex-shrink: 0;
}

.tree-node.dept-node {
  font-weight: 600;
  color: #303133;
}

.tree-node.dept-node .dept-name {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tree-node.dept-node .dept-count {
  color: #999;
  font-size: 12px;
  margin-right: 10px;
  flex-shrink: 0;
}

.tree-node.user-node {
  color: #606266;
  padding-right: 10px;
  width: 100%;
}

.tree-node.user-node .nickname {
  margin-left: 6px;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>